import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DISPLAY_NAME_LENGTH } from '@cribbage/protocol';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '~/api/client.js';
import { useAnonymous, useAuthStore } from '~/store/authStore.js';
import { refusalLine, toFriendlyError } from '~/store/sessionStore.js';
import { Banner } from '~/ui/Banner.js';
import { ButtonLink } from '~/ui/Button.js';
import { ImportPrompt } from '~/ui/ImportPrompt.js';
import { Panel } from '~/ui/Panel.js';
import { TextField } from '~/ui/TextField.js';
import { useLatestRequest } from '~/ui/useLatestRequest.js';
import { emailProblem, newPasswordHint, newPasswordProblem } from '../accountChecks.js';
import { useAuthEntry } from '../useAuthEntry.js';
import type { AccountFlash } from './AccountPage.js';
import { ACCOUNT_PATH, accountPath, carryResume, resumeOf } from './accountPages.js';
import { SubmitButton } from './SubmitButton.js';

/**
 * The ways into an account from inside Settings: the wall behind a guest's
 * Change chips, Create an account, Sign in, and the two pages that get a
 * forgotten password back.
 *
 * They ask the same questions `/auth` asks, through the same `useAuthEntry`,
 * and differ only in the frame: the question came from Settings, so it is
 * answered there and lands back on Account. A page that sent a guest here to
 * sign in first, such as the offer, hands its way back in router state
 * (`resumeState`), and a finished sign-in or account goes there instead, with
 * that page's own state. Sign in and Create an account pass it to each other.
 * All but the two password pages are
 * a guest's. Whoever already has an account is sent back to Account, which is
 * where the chip that opened the page was.
 */

/**
 * Back to Account, carrying the one line it should open with. Or, when the
 * page that sent the guest here handed a way back, back to that page as it
 * was left: the line is Account's, so it stays behind.
 */
function useBackToAccount(): (flash?: AccountFlash) => void {
  const navigate = useNavigate();
  const resume = resumeOf(useLocation().state);
  return (flash) => {
    if (resume !== null) {
      void navigate(resume.to, { replace: true, state: resume.state ?? null });
      return;
    }
    void navigate(ACCOUNT_PATH, { replace: true, state: flash === undefined ? null : { flash } });
  };
}

/**
 * A guest's page only. `busy` holds the redirect off while a submit is in the
 * air, because the store says "account" the moment a sign-in lands, and the
 * page still has the import question to ask or a flash to hand over.
 */
function useGuestOnly(busy: boolean): void {
  const anonymous = useAnonymous();
  const back = useBackToAccount();
  useEffect(() => {
    if (!anonymous && !busy) back();
    // `back` is a fresh closure each render and changes nothing it reads.
  }, [anonymous, busy]);
}

/** The question as a line, the answer as a quiet chip under it. */
function OtherDoor({
  line,
  label,
  to,
}: {
  readonly line: string;
  readonly label: string;
  readonly to: string;
}): React.JSX.Element {
  // The way back a page handed this one goes on to the other door.
  const carried = carryResume(useLocation().state);
  return (
    <div className="flex flex-col items-center gap-1 text-center">
      <p className="text-muted">{line}</p>
      <ButtonLink variant="ghost" to={to} replace state={carried}>
        {label}
      </ButtonLink>
    </div>
  );
}

/**
 * The wall a guest meets behind a Change chip. What needs an account sits
 * above an untitled panel and names it; the panel carries the reason and the
 * one lemon; the sign-in question sits under it (Mitch, 2026-09-21).
 */
export function NeedAccountPage({
  wanted,
}: {
  readonly wanted: 'name' | 'avatar';
}): React.JSX.Element {
  const { t } = useTranslation();
  const need = useId();
  useGuestOnly(false);
  return (
    <>
      <p id={need} className="text-center text-ink">
        {t(wanted === 'avatar' ? 'settings.account.needAvatar' : 'settings.account.needName')}
      </p>
      <Panel aria-labelledby={need}>
        <p className="text-muted">{t('settings.account.guestWhy')}</p>
        <ButtonLink variant="primary" to={accountPath('register')}>
          {t('settings.account.createAccount')}
        </ButtonLink>
      </Panel>
      <OtherDoor
        line={t('settings.account.haveAccount')}
        label={t('auth.signIn')}
        to={accountPath('sign-in')}
      />
    </>
  );
}

/**
 * Create an account. The name field is where the guest trades the name the
 * game gave them for one they chose; registering adopts this device's player,
 * so every game already played comes with it and there is nothing to import.
 */
export function RegisterPage(): React.JSX.Element {
  const { t } = useTranslation();
  const back = useBackToAccount();
  const loading = useAuthStore((state) => state.loading);
  const serverError = useAuthStore((state) => state.error);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const entry = useAuthEntry(() => {
    back('joined');
  });
  const { problems, refusal } = entry;
  useGuestOnly(submitting);

  // A refusal left on the store by another screen is not this form's.
  useEffect(() => {
    entry.reset();
  }, []);

  /* Rails names the fields it refused. The line under each field is theirs; a
     refusal that names none of them is the banner's. */
  const refused = (field: string): string | undefined => {
    const messages = serverError?.details?.[field];
    return messages === undefined ? undefined : refusalLine(field, messages);
  };
  const named = ['display_name', 'email', 'password'].some(
    (field) => serverError?.details?.[field] !== undefined,
  );

  return (
    <form
      className="flex flex-col gap-6"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        setSubmitting(true);
        void entry.register({ email, password, displayName }).then(() => {
          // A refusal leaves the player a guest, and the form is theirs again.
          // A success has already left for Account with its flash, and must
          // not let the guest-only redirect leave again without it.
          if (useAuthStore.getState().user?.anonymous ?? true) setSubmitting(false);
        });
      }}
    >
      {refusal !== null && <Banner role="alert">{named ? t('auth.problems') : refusal}</Banner>}
      <div className="flex flex-col gap-5">
        <TextField
          label={t('auth.displayName')}
          hint={t('auth.displayNameHint')}
          type="text"
          autoComplete="nickname"
          maxLength={DISPLAY_NAME_LENGTH.max}
          value={displayName}
          error={problems.displayName ?? refused('display_name')}
          onChange={(event) => {
            setDisplayName(event.target.value);
          }}
        />
        <TextField
          label={t('auth.email')}
          hint={t('auth.emailHint')}
          type="email"
          autoComplete="email"
          value={email}
          error={problems.email ?? refused('email')}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        <TextField
          label={t('auth.password')}
          hint={newPasswordHint(t)}
          type="password"
          autoComplete="new-password"
          value={password}
          error={problems.password ?? refused('password')}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
      </div>
      <SubmitButton busy={loading}>{t('auth.register')}</SubmitButton>
      <OtherDoor
        line={t('settings.account.haveAccount')}
        label={t('auth.signIn')}
        to={accountPath('sign-in')}
      />
    </form>
  );
}

/** What the new-password page hands Sign in, or Account, when it lands there. */
interface ResetState {
  readonly flash?: 'passwordReset';
}

/**
 * Sign in, for a player who already has an account: a returning player on a
 * new device, or one who played as a guest first.
 *
 * The refusal names neither field, so it cannot confirm which email has an
 * account. It sits under the password, the last field before the lemon, and
 * both values stay. After a sign-in the one-time offer to bring this device's
 * games along may open, and Account follows once it is answered.
 *
 * Forgot your password? is a ghost under the lemon, as the sketch draws it.
 * Apple and Google are drawn in the sketch and not here: neither is wired in
 * the client yet.
 */
export function SignInPage(): React.JSX.Element {
  const { t } = useTranslation();
  const back = useBackToAccount();
  const loading = useAuthStore((state) => state.loading);
  // A new password lands here, with the one line that says it worked.
  const reset = (useLocation().state as ResetState | null)?.flash === 'passwordReset';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const entry = useAuthEntry(() => {
    back();
  });
  const { problems, refusal, signingIn, importing, importBusy, importError, answerImport } = entry;
  useGuestOnly(signingIn || importing !== null);

  useEffect(() => {
    entry.reset();
  }, []);

  return (
    <>
      {reset && (
        <Banner role="status" tone="good">
          {t('settings.account.passwordSaved')}
        </Banner>
      )}
      <form
        className="flex flex-col gap-6"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void entry.signIn({ email, password, displayName: '' });
        }}
      >
        <div className="flex flex-col gap-5">
          <TextField
            label={t('auth.email')}
            type="email"
            autoComplete="email"
            value={email}
            error={problems.email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <TextField
            label={t('auth.password')}
            type="password"
            autoComplete="current-password"
            value={password}
            error={problems.password ?? refusal ?? undefined}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </div>
        <div className="flex flex-col gap-3">
          <SubmitButton busy={loading || signingIn}>{t('auth.signIn')}</SubmitButton>
          <ButtonLink variant="ghost" to={accountPath('forgot-password')}>
            {t('settings.account.forgotPassword')}
          </ButtonLink>
        </div>
      </form>
      <OtherDoor
        line={t('settings.account.noAccount')}
        label={t('settings.account.createAccount')}
        to={accountPath('register')}
      />
      {importing !== null && (
        <ImportPrompt
          gamesCount={importing.games_count}
          busy={importBusy}
          error={importError}
          onAccept={() => {
            void answerImport(true);
          }}
          onDecline={() => {
            void answerImport(false);
          }}
        />
      )}
    </>
  );
}

/**
 * Forgot your password: one email field, and a link sent to it.
 *
 * The page says the same thing whether or not an account uses the address,
 * as the server does (`AuthController#password_reset`), so neither can be
 * used to learn who plays here. Sending again is allowed, for a typo or an
 * email that never came; the server's rate limit is the brake.
 *
 * Open to anyone, like the new-password page: its "Get a new link" lands here,
 * and that link may be opened on a device signed in to an account. The request
 * carries no session. Only the way back to Sign in is a guest's.
 */
export function ForgotPasswordPage(): React.JSX.Element {
  const { t } = useTranslation();
  const anonymous = useAnonymous();
  const request = useLatestRequest();
  const [email, setEmail] = useState('');
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async (): Promise<void> => {
    const invalid = emailProblem(email, t);
    if (invalid !== undefined) {
      setProblem(invalid);
      return;
    }
    const isCurrent = request.begin();
    setProblem(undefined);
    setFailure(null);
    setBusy(true);
    try {
      await api.requestPasswordReset(email.trim());
      if (isCurrent()) setSent(true);
    } catch (error) {
      if (!isCurrent()) return;
      setSent(false);
      setFailure(t(toFriendlyError(error).key));
    } finally {
      if (isCurrent()) setBusy(false);
    }
  };

  return (
    <>
      {sent && <Banner role="status">{t('settings.account.linkSent')}</Banner>}
      {failure !== null && <Banner role="alert">{failure}</Banner>}
      <form
        className="flex flex-col gap-6"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <TextField
          label={t('auth.email')}
          hint={t('settings.account.forgotHint')}
          type="email"
          autoComplete="email"
          value={email}
          error={problem}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        <SubmitButton busy={busy}>{t('settings.account.sendLink')}</SubmitButton>
      </form>
      {anonymous && (
        <OtherDoor
          line={t('settings.account.rememberPassword')}
          label={t('auth.signIn')}
          to={accountPath('sign-in')}
        />
      )}
    </>
  );
}

/**
 * The page the reset email's link opens. The link is `/reset-password`, which
 * the app sends here with its token (`RESET_LINK_PATH`). One field: the email
 * has already proved who is asking.
 *
 * It signs nobody in. The server revokes every session the account had, so the
 * way on is Sign in, with a line saying the new password is saved. A device
 * that was signed in to that same account has just lost its session too, so
 * the page has the store check before it decides where to go, rather than
 * leave the player on an Account page that fails on its next request.
 *
 * Open to anyone, unlike the entry pages above: the link may be opened on a
 * device signed in to another account, and it still has a password to set.
 *
 * A link that ran out or was used is not a refusal of the field. The form gives
 * way to a panel that says so, with the one way on: ask for another.
 */
export function NewPasswordPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const request = useLatestRequest();
  const why = useId();
  const token = useSearchParams()[0].get('token') ?? '';
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | null>(null);
  const [spent, setSpent] = useState(token === '');
  const [busy, setBusy] = useState(false);

  const save = async (): Promise<void> => {
    const short = newPasswordProblem(password, t);
    if (short !== undefined) {
      setProblem(short);
      return;
    }
    const isCurrent = request.begin();
    setProblem(undefined);
    setFailure(null);
    setBusy(true);
    try {
      await api.confirmPasswordReset(token, password);
    } catch (error) {
      if (!isCurrent()) return;
      setBusy(false);
      if (error instanceof ApiError && error.code === 'invalid_token') {
        setSpent(true);
        return;
      }
      const refused = error instanceof ApiError ? error.details?.password : undefined;
      if (refused === undefined) setFailure(t(toFriendlyError(error).key));
      else setProblem(refusalLine('password', refused));
      return;
    }
    // A page the player has left goes nowhere, and a revoked session is found
    // by the store's next request anyway.
    if (!isCurrent()) return;
    const auth = useAuthStore.getState();
    if (!(auth.user?.anonymous ?? true)) await auth.refresh();
    if (!isCurrent()) return;
    const signedIn = !(useAuthStore.getState().user?.anonymous ?? true);
    const done: ResetState = { flash: 'passwordReset' };
    void navigate(signedIn ? ACCOUNT_PATH : accountPath('sign-in'), { replace: true, state: done });
  };

  if (spent) {
    return (
      <>
        <p id={why} className="text-center text-ink">
          {t('settings.account.linkSpent')}
        </p>
        <Panel aria-labelledby={why}>
          <p className="text-muted">{t('settings.account.linkSpentWhy')}</p>
          <ButtonLink variant="primary" to={accountPath('forgot-password')} replace>
            {t('settings.account.newLink')}
          </ButtonLink>
        </Panel>
      </>
    );
  }

  return (
    <>
      {failure !== null && <Banner role="alert">{failure}</Banner>}
      <form
        className="flex flex-col gap-6"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <TextField
          label={t('settings.account.newPassword')}
          hint={newPasswordHint(t)}
          type="password"
          autoComplete="new-password"
          value={password}
          error={problem}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
        <SubmitButton busy={busy}>{t('settings.account.savePassword')}</SubmitButton>
      </form>
    </>
  );
}
